import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import config from '../../config.js';
import '../../styles/global.css';
import '../../styles/status.css';
import '../../styles/pagination.css';

import PageHeader from '../../components/general/pageHeader.js';
import PageTable from '../../components/general/pageTable';
import AmenityBooking from '../../components/amenities/AmenityBooking.js';
import useAuthenticate from '../../components/authentication/authenticate';
import Loading from '../../components/general/loading.js';
import AmenityCards from '../../components/cards/amenityCard.js';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

const RAmenities = () => {
  const { user, isLoading } = useAuthenticate();
  const queryClient = useQueryClient();

  const role = Object.keys(user?.user?.active || {})[0];
  const accountId = user?.user?.accounts?.[role];

  const [selectedProperty, setSelectedProperty] = useState(null);
  const [view, setView] = useState("table");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const { data: properties } = useQuery({
    queryKey: ['properties'],
    queryFn: fetchProperties,
  });

  const { data: bookingData = {}, isLoading: isTableLoading } = useQuery({
    queryKey: ['bookings', selectedProperty?.value, accountId, currentPage],
    queryFn: () => fetchBookingsByProperty(selectedProperty?.value, accountId, currentPage, rowsPerPage),
    enabled: !!selectedProperty?.value && !!accountId,
    keepPreviousData: true,
  });

  const bookings = bookingData.results || [];
  const totalPages = bookingData.totalPages || 1;

  useEffect(() => {
    if (properties?.length > 0 && !selectedProperty) {
      setSelectedProperty(properties[0]);
    }
  }, [properties, selectedProperty]);

  const handleBookingComplete = () => {
    queryClient.invalidateQueries(['bookings']);
  };

  const maxVisibleButtons = 10;
  const startPage =
    Math.floor((currentPage - 1) / maxVisibleButtons) * maxVisibleButtons + 1;
  const endPage = Math.min(startPage + maxVisibleButtons - 1, totalPages);

    if (view === "booking") {
        return <AmenityBooking 
                    onBack={() => setView("table")}
        onBookingComplete={handleBookingComplete}
        />;
    }

  return (
    <div className="full-page w-full">
      <PageHeader pageName="Amenity Bookings" />

      {isLoading ? (
        <Loading />
      ) : (
        <>
          <div className="page-header flex flex-col md:flex-row gap-3">
            <div className="w-full md:w-[300px]">
              <Autocomplete
                options={properties}
                value={selectedProperty}
                onChange={(e, newVal) => {
                  if (newVal && newVal.value && newVal.label) {
                    setSelectedProperty(newVal);
                    setCurrentPage(1);
                  } else {
                    setSelectedProperty(null);
                  }
                }}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                getOptionLabel={(option) =>
                  typeof option === 'string'
                    ? option
                    : option?.label || option?.name || ''
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Property"
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </div>

            <div className="flex md:flex-row gap-1 w-full md:w-auto mt-2 md:mt-0">
              <div className="w-full md:w-auto">
                <button
                  className="header-button w-full md:w-auto h-[50px] md:h-full text-[14px] md:text-[12px]"
                                    onClick={() => setView("booking")}
                >
                  <i className="fas fa-water-ladder"></i> Book Amenity
                </button>
              </div>
            </div>

          </div>

          {/* Table or Cards */}
          {isTableLoading ? (
            <Loading />
          ) : (
            <div className="table-wrapper">
              {/* Mobile Cards */}
              <div className="block md:hidden mt-4">
                <AmenityCards rows={bookings} />
              </div>

              {/* Desktop Table */}
              <div className="hidden md:block">
                <PageTable
                  rows={bookings}
                  columns={[
                    {
                      label: 'Amenity',
                                            accessor: (row) => row.amenity?.name || 'N/A', // requires populate('amenity')
                                            width: '20%',
                                        },
                                        {
                                            label: 'Date',
                                            accessor: (row) =>
                                            new Date(row.bookingDate).toLocaleDateString(),
                                            width: '15%',
                                        },
                                        {
                                            label: 'Booked by',
                                            accessor: (row) => row.bookedBy?.user?.givenName + ' ' + row.bookedBy?.user?.lastName || 'N/A',
                                            width: '15%',
                                        },
                                        {
                                            label: 'From',
                                            accessor: (row) => `${row.startTime}`,
                                            width: '10%',
                                        },
                                        {
                                            label: 'To',
                                            accessor: (row) => `${row.endTime}`,
                                            width: '10%',
                                        },
                                        {
                                            label: 'Status',
                                            accessor: (row) => (
                                            <span className={`amenity-status-badge ${row.status?.toLowerCase()}`}>
                                                {row.status}
                                            </span>
                                            ),
                                            width: '15%',
                                        },
                                        {
                                            label: 'Notes',
                                            accessor: (row) => row.notes || '-',
                                            width: '10%',
                    },
                  ]}
                  hasButtons={false}
                />
              </div>

              {totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-buttons">
                    <button
                      className="pagination-button"
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>

                    {startPage > 1 && (
                      <button className="pagination-page" onClick={() => setCurrentPage(startPage - 1)} >
                        ...
                      </button>
                    )}

                    {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                      const pageNum = startPage + i;
                      return (
                        <button
                          key={pageNum}
                          className={`pagination-page ${ currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    {endPage < totalPages && (
                      <button className="pagination-page" onClick={() => setCurrentPage(endPage + 1)}>
                        ...
                      </button>
                    )}

                    <button
                      className="pagination-button"
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const fetchBookingsByProperty = async (propertyId, accountId) => {
    if (!propertyId || !accountId) return [];

    try {
        const response = await axios.get(`${config.API_URL}/v2/amenity/booking`, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            },
            params: {
                propertyId: propertyId,
                bookedBy: accountId,
            }
        });

        const bookings = response.data.results || response.data || [];
        
        console.log('Fetched bookings for property:', bookings);
        return bookings;
    } catch (error) {
        console.error('❌ Error fetching bookings for property:', propertyId, error);
        return [];
    }
};

const fetchProperties = async () => {
    const response = await axios.get(`${config.API_URL}/v2/property`, {headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`}});
    return response.data.map(item => ({
    value: item._id,
    label: item.name,
  }));
};


export default RAmenities;